import React, { memo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';

import { authSelectors } from 'app-components/flux/auth';
import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { rootUiSelectors } from 'app-components/flux/root-ui';

import { DIALOG_TYPE as TRANSACTION_RULE_DIALOG_TYPE } from 'components/Dialogs/TransactionRules/actions';

import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Slide from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';

import { preferencesSelectors } from 'app-components/flux/preferences';

import { makeStyles } from 'tss-react/mui';

import SimplifiAndQBPLogo from 'components/SimplifiAndQBPLogo';
import { useAppSelector } from 'hooks/redux';
import { tracker } from 'app-components/utils/core';
import { HoverProvider, useHoverStore, useIsInteractive } from './HoverContext';
import { RefreshNavItem } from './NavItems';
import { NavPopoverV2 } from './Popover';
import { navBarWidth, useNavBarStyle } from './commonNavStyles';

const useStyles = makeStyles()((theme) => ({
  staticNav: {
    zIndex: theme.zIndex.drawer,
    backgroundColor: theme.palette.background.paper,
    width: navBarWidth,
    left: 0,
    height: '100%',
  },
  navItemsHolder: {
    display: 'flex',
    flexDirection: 'column-reverse',
    marginTop: 0,
    textAlign: 'left',
    width: '100%',
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0,
    padding: 0,
    alignItems: 'baseline',
    border: 0,
  },
  rootButton: {
    minWidth: 'unset',
  },
  logoSection: {
    height: theme.defaults?.headers.subHeaderHeightTall,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  navButtonBottom: {
    display: 'flex',
    flexDirection: 'column',
    flexFlow: 'column',
    textAlign: 'center',
    textColor: '#333',
  },
  betaLabel: {
    flex: 'auto',
    fontSize: '18px',
    fontWeight: 200,
    letterSpacing: '.05rem',
    position: 'absolute',
    right: 5,
    top: 10,
  },
  appDiv: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: navBarWidth,
  },
  appBarContainer: {
    overflowY: 'auto',
    maxWidth: '100%',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.applyOpacityToHex?.(theme.palette.icon.main, 0.5)} ${theme.palette.background.paper}`,
    background: theme.palette.background.paper,
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.applyOpacityToHex?.(theme.palette.icon.main, 0.5),
      borderRadius: 8,
    },
    '&::-webkit-scrollbar': {
      backgroundColor: theme.palette.background.paper,
      width: 5,
    },
  },
  navBottomSection: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: 'auto',
    marginBottom: 32,
    overflow: 'hidden',
    flexShrink: 0,
  },
}));

const Header = React.memo(function Header() {
  const { classes } = useStyles();
  const { classes: navBarClasses } = useNavBarStyle();

  const authenticated: boolean = useAppSelector((state) => authSelectors.isAuthenticated(state));
  const hideNavButtons = useAppSelector(
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'gsfScreen', 'completed_gsf') !== 'completed_gsf',
  );
  const disableNav = useAppSelector((state) =>
    (rootUiSelectors.getActiveModalsAsJS(state) as Array<{ type: string }>).some(
      ({ type }) => type === TRANSACTION_RULE_DIALOG_TYPE,
    ),
  );
  const kioskmode: boolean = useAppSelector(
    (state) => preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] }).kioskmode,
  );

  const store = useHoverStore();
  const isInteractive = useIsInteractive();

  const closeDrawer = useCallback(() => {
    store.setOverBar(false);
    store.setOverPop(false);
  }, [store]);

  const handleLogoClick = useCallback(() => {
    if (!hideNavButtons) {
      tracker.track(tracker.events.navMenuClick, { menuItem: 'Logo', section: 'main' });
    }
    closeDrawer();
  }, [hideNavButtons, closeDrawer]);

  return (
    <Slide in={authenticated} direction='right'>
      <AppBar
        id='application-header-bar'
        elevation={0}
        position='fixed'
        className={
          hideNavButtons ? classes.staticNav : isInteractive ? navBarClasses.navbar : navBarClasses.navbarClosed
        }
        onMouseLeave={() => store.setOverBar(false)}
        onMouseEnter={() => store.setOverBar(true)}
      >
        <div
          className={classes.appDiv}
          data-testid='nav-bar-content'
          style={disableNav ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          <div className={classes.logoSection} style={{ cursor: hideNavButtons ? 'default' : undefined }}>
            <Button
              onClick={handleLogoClick}
              id='logo-nav'
              classes={{
                root: classes.rootButton,
              }}
              component={NavLink}
              to='/'
              disabled={hideNavButtons}
              style={{ cursor: hideNavButtons ? 'default' : undefined }}
            >
              <SimplifiAndQBPLogo showLogo />
            </Button>
          </div>
          <div className={classes.appBarContainer}>
            <Toolbar classes={{ root: classes.toolbar }}>
              <div className={classes.betaLabel}>{`${kioskmode ? '*' : ''}`}</div>

              {!hideNavButtons && (
                <div key='main-nav-items' className={classes.navItemsHolder}>
                  <div>
                    <NavPopoverV2 />
                  </div>
                </div>
              )}
            </Toolbar>
          </div>
          <div className={classes.navBottomSection}>
            <div className={classes.navButtonBottom}>
              <div className={navBarClasses.groupDividerStatic} />
              {!hideNavButtons && <RefreshNavItem key='refresh-all-button' closeDrawer={closeDrawer} />}
            </div>
          </div>
        </div>
      </AppBar>
    </Slide>
  );
});

const AppHeader = React.memo(function AppHeader() {
  return (
    <HoverProvider>
      <Header />
    </HoverProvider>
  );
});

export default memo(AppHeader);
